// Lib
import { oc } from "ts-optchain";

// Vue
import Vue from "vue";
import { mapGetters } from "vuex";

// Icons
import { faAngleLeft } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/vue-fontawesome";

// Services
import { AddressBookService, ObjectUtil, ShoppingCartService, CurrencyService, ErrorLogService } from "@/services";

// Mixins
import { formatDateTime, stringHelper } from "@/components/mixins";

// Components
import { CurrencyText, LoadingSpinner, PostalAddress, StaticFormControl } from "@/components";
import { CartContentWrapper } from "@/components/Cart/CartContentWrapper";
import { default as CartPromos } from "../Controls/CartPromos/CartPromos.vue";
import { default as CartTotals } from "../Controls/CartTotals/CartTotals.vue";

// Types
import {
  AddressType,
  IOrderResponseDto,
  OrderPlacementStatus, 
  OrderStatus,
  IFreightChargeLogDto} from "@/types";
import { 
  CartStepName, 
  IShoppingCartContext } from "../Types";

export default Vue.extend({
  name: "Step3",
  components: {
    FontAwesomeIcon,
    CartContentWrapper,
    CurrencyText,
    LoadingSpinner,
    PostalAddress,
    StaticFormControl,
    CartPromos,
    CartTotals,
  },
  mixins: [formatDateTime, stringHelper],
  data: () => ({
    backIcon: faAngleLeft,

    billToAddress: null,
    shipToAddress: null,
    finalDeliveryAddress: null,
    orderType: "",
    poNumber: "",
    partsFreeLabel: "",
    stockShipViaMessage: "",
    shippingWarehouses: null,
    thirdParyAccountsMessage: "",
    freightTermsLocalizationKey: "",
    freightCost: null,
    submittingOrder: false,
    errorMessage: undefined,
  }),
  computed: {
    ...mapGetters([
      "user", 
      "getContentItem", 
      "canSeeContractPrice", 
      "canSeeAnyPrice", 
      "billingCurrencyCode",
      "hasPermission", 
      "activeAccountId",
      "facingWarehouse"
    ]),

    items() {
      return oc(this.context as IShoppingCartContext).cartData.Items([]);
    },

    showOfflineHeader(){
      return this.context.showOfflineToWarehouseMessageHeader === true;
    },
    hasPromotions() {
      return oc(this.context as IShoppingCartContext).cartData.PromotionMessages([]).length > 0;
    },
    
    hasFedExShipping() {
      return this.context.warehouses?.some(warehouse => warehouse.carrierDescription === "FedEx") ?? false;
    },

    hasUpsShipping() {
      return this.context.warehouses?.some(warehouse => warehouse.carrierDescription === "UPS") ?? false;
    },
  },
  props: {
    context: {
      type: Object,
      default: () => ObjectUtil.createInstance<IShoppingCartContext>(),
    },
  },
  mounted() {
    const ctx = this.context as IShoppingCartContext;

    const orderOptions = ctx.orderOptions!;

    this.orderType = orderOptions.ShipmentType;
    this.poNumber = orderOptions.PoNumber;
    this.freightTermsLocalizationKey = orderOptions.FreightTerm;

    const billToAddress = ctx.branch.Addresses.find((x) => x.AddressType === AddressType.Mailing);
    
    this.billToAddress = AddressBookService.ConvertAccountAddressToPostalAddress(billToAddress);

    if (orderOptions.IsShipToCustomer) {
      this.shipToAddress = {
        Name: orderOptions.CustomerAddressName,
        Name2: orderOptions.CustomerAddressName2,
        AddressLine1: orderOptions.CustomerAddress1,
        AddressLine2: orderOptions.CustomerAddress2,
        City: orderOptions.CustomerCity,
        CountryCode: orderOptions.CustomerCountryCode,
        PostalCode: orderOptions.CustomerPostalCode,
        StateProvinceCode: orderOptions.CustomerStateProvince,
      };
    } else if (orderOptions.IsEmergencyShipTo) {
      this.shipToAddress = {
        Name: orderOptions.EmergencyAddressName,
        AddressLine1: orderOptions.EmergencyAddress1,
        AddressLine2: orderOptions.EmergencyAddress2,
        City: orderOptions.EmergencyCity,
        StateProvinceCode: orderOptions.EmergencyStateProvince,
        PostalCode: orderOptions.EmergencyPostalCode,
        CountryCode: orderOptions.EmergencyCountryCode,
      };
    } else {
      const shipToAddress = ctx.branch.Addresses.find((x) => x.AddressType === AddressType.PartsShipTo);
      this.shipToAddress = AddressBookService.ConvertAccountAddressToPostalAddress(shipToAddress);
    }

    if (orderOptions.IsFinalToCustomer) {
      this.finalDeliveryAddress = {
        Name: orderOptions.FinalToCustomerAddressName,
        AddressLine1: orderOptions.FinalToCustomerAddress1,
        AddressLine2: orderOptions.FinalToCustomerAddress2,
        City: orderOptions.FinalToCustomerCity,
        StateProvinceCode: orderOptions.FinalToCustomerStateProvince,
        PostalCode: orderOptions.FinalToCustomerPostalCode,
        CountryCode: orderOptions.FinalToCustomerCountryCode,
      }
    }

    this.shippingWarehouses = ctx.warehouses?.map(function(x){
      var shippingMethod = x.value;
      if(ctx.orderOptions?.IsPartsFastPartsFree)
      {
        shippingMethod = x.pfpfValue;
      }      
      return {
        name: x.description,
        shippingMethod: shippingMethod,
      }
     });

    const thirdPartyList: string[] = [];

    if (this.hasUpsShipping && orderOptions.UpsShippingAccount) {
      thirdPartyList.push(`UPS ${orderOptions.UpsShippingAccount}`);
    }

    if (this.hasFedExShipping && orderOptions.FedexShippingAccount) {
      thirdPartyList.push(`FedEx ${orderOptions.FedexShippingAccount}`);
    }

    orderOptions.Other3pShippingAccounts?.forEach((x) => {
      let carrierDescription = x?.ShippingOption?.CarrierDescription;
      if(carrierDescription === "Unknown" ||  carrierDescription === "N/A"){
        carrierDescription = x?.ShippingOption?.ShippingMethodCode;
      }
    
      if(carrierDescription){
        thirdPartyList.push(`${carrierDescription} ${x.AccountNumber}`);
      }    
    });

    if(this.freightTermsLocalizationKey != "Prepaid"){
      this.thirdParyAccountsMessage = thirdPartyList.join("<br>");
    }

    this.freightCost = ctx.cartData.FreightTotal;

    this.getFreightCharge();
  },
  methods: {
    navigateToStep(stepName: CartStepName) {
      this.$emit("on-step-change", stepName);
    },

    goToStep1() {
      this.navigateToStep(CartStepName.Step1);
    },

    async submitOrder() {
      this.submittingOrder = true;
      const ctx = this.context as IShoppingCartContext;

      const accountId = ctx.accountId;
      const user = ctx.user;
      const freightChargeLogDto = ctx.freightChargeLogDto;

      let formattedTotalPrice = CurrencyService.formatCurrency(ctx.cartData.PartsTotal, this.billingCurrencyCode)

      ctx.orderOptions!.AccountId = +ctx.branch.AccountId;
      ctx.orderOptions.IsPartsStoreOrder = ctx.isPartsStoreOrder;
      try{
        const orderResponse = await ShoppingCartService.PlaceOrder({
          accountId,
          user: user,
          shoppingCartId: ctx.cartId,
          formattedTotalPrice: formattedTotalPrice,
          orderOptions: ctx.orderOptions!,
          liftLinkForm: ctx.liftLinkForm
        });

        this.submittingOrder = false;
        if (this.isSuccessfulOrder(orderResponse)) {
          if( this.isOfflineOrder(orderResponse)){
            this.context.isOfflineToSap = true;
          }
          this.$emit("on-place-order", orderResponse);

          /* not all shipping codes are set-up with a charge yet, so only log when charge is present */
          if (freightChargeLogDto?.FreightCharge) {
            // order number is null on CSD orders, set it to zero
            freightChargeLogDto.OrderNumber = orderResponse[0].OrderNumber === "" ? "0" : orderResponse[0].OrderNumber;
            freightChargeLogDto.PONumber = orderResponse[0].PONumber;
            
            this.$emit("log-freight-charge", freightChargeLogDto);
          }

          // return orderType back to Emergency for the next order
          ShoppingCartService.setOrderTypePref("Emergency");
          // remove promos from local storage after a successful order
          localStorage.removeItem("shopping-cart-promo-codes");

          this.navigateToStep(CartStepName.ThankYou);
        } else {
          throw 'The orderResponse did not indicate a successful order.'  
        }
      }catch(err){      
        ErrorLogService.LogException(err);

        this.submittingOrder = false;  
        window.scrollTo(0, 0);
          
        this.$store.commit("pushTimedMessage", {
          header: "Order Failed to Submit",
          message: "Your order failed to submit. Please try again, and contact support if this problem continues.",
          IsError: true,
          seconds: 60
        })
      }
    },

    isSuccessfulOrder(orderResponse: IOrderResponseDto[]){
      let isSuccessfulOrder = !orderResponse.some(x => x.OrderStatus === OrderPlacementStatus.NotPlaced || x.OrderStatus === OrderPlacementStatus.Failed);
      return isSuccessfulOrder;
    },

    isOfflineOrder(orderResponse: IOrderResponseDto[]){
      let isSuccessfulOrder = orderResponse.some(x => x.OrderStatus === OrderPlacementStatus.Offline);
      return isSuccessfulOrder;
    },

    async getFreightCharge() {
      var partsTotal = this.context.cartData.PartsTotal;
      var shippingMethod = this.shippingWarehouses != undefined ? this.shippingWarehouses[0].shippingMethod : "";

      try {
        await ShoppingCartService.getFreightCharge(
          partsTotal,
          shippingMethod
        ).then( (results) =>{
          if (results.data) {
            this.setFreightCharge(results.data);
          }
        }); 
      } catch (err) {
        ErrorLogService.LogException(err);
      }    
    },

    setFreightCharge(freightCharge: IFreightChargeLogDto) {
      if(!freightCharge) return;
      
      let shippingCode = this.shippingWarehouses != undefined ? this.shippingWarehouses[0].shippingMethod : "";

      freightCharge.ShippingCode = shippingCode;
      freightCharge.OrderCost = this.context.cartData.PartsTotal;
      freightCharge.AccountID = this.activeAccountId;
      freightCharge.ExternalUserId = this.user.UserId;
      freightCharge.FacingWarehouseCode = this.shippingWarehouses[0].name.slice(-2);
      freightCharge.CreatedBy = this.user.UserId;
      freightCharge.CreatedDate = null;

      this.$emit("freight-charge", freightCharge);
    }
  },
});
